import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaHardHat, FaShieldAlt, FaGlasses, FaVest, FaArrowRight, FaPhone, FaCheckCircle } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const PPESolutionsPage = () => {
    const products = [
        {
            icon: <FaHardHat size={32} />,
            title: 'Safety Helmets & Hard Hats',
            description: 'ANSI-certified hard hats and safety helmets providing impact protection in data center environments.',
            gradient: 'from-yellow-600 to-amber-600'
        },
        {
            icon: <FaShieldAlt size={32} />,
            title: 'Arc Flash Protective Clothing',
            description: 'Flame-resistant arc flash rated clothing ensuring protection against electrical hazards.',
            gradient: 'from-orange-600 to-red-600'
        },
        {
            icon: <FaGlasses size={32} />,
            title: 'Electrical Safety Gloves',
            description: 'Insulated electrical gloves rated for various voltage levels with superior grip and dexterity.',
            gradient: 'from-blue-600 to-indigo-600'
        },
        {
            icon: <FaGlasses size={32} />,
            title: 'Safety Glasses & Face Shields',
            description: 'Impact-resistant eye protection and face shields meeting ANSI Z87.1 standards.',
            gradient: 'from-purple-600 to-pink-600'
        },
        {
            icon: <FaVest size={32} />,
            title: 'High-Visibility Safety Vests',
            description: 'ANSI Class 2 and 3 high-visibility vests for improved visibility in data center facilities.',
            gradient: 'from-green-500 to-emerald-600'
        },
        {
            icon: <FaShieldAlt size={32} />,
            title: 'Anti-Static PPE & ESD Protection',
            description: 'Static-dissipative clothing and grounding equipment to protect sensitive electronics.',
            gradient: 'from-cyan-500 to-blue-600'
        },
        {
            icon: <FaHardHat size={32} />,
            title: 'Protective Footwear & Safety Boots',
            description: 'Steel-toed safety boots with slip-resistant soles and electrical hazard protection.',
            gradient: 'from-teal-600 to-cyan-600'
        },
        {
            icon: <FaShieldAlt size={32} />,
            title: 'Respiratory Protection Equipment',
            description: 'N95/N100 respirators and supplied air systems for various hazardous environments.',
            gradient: 'from-indigo-600 to-violet-600'
        },
        {
            icon: <FaHardHat size={32} />,
            title: 'Hearing Protection Devices',
            description: 'Noise-reducing earplugs and earmuffs with communication capability.',
            gradient: 'from-rose-600 to-pink-600'
        },
        {
            icon: <FaShieldAlt size={32} />,
            title: 'Lockout/Tagout Safety Kits',
            description: 'Comprehensive LOTO kits with locks, tags, cables, and hasps for equipment safety.',
            gradient: 'from-red-600 to-rose-600'
        },
        {
            icon: <FaHardHat size={32} />,
            title: 'Fire-Resistant Clothing',
            description: 'FR-rated apparel meeting NFPA 70E standards for maximum thermal protection.',
            gradient: 'from-orange-500 to-amber-600'
        },
        {
            icon: <FaVest size={32} />,
            title: 'Hand Protection & Industrial Gloves',
            description: 'Nitrile, latex, and leather gloves for various data center operations and maintenance tasks.',
            gradient: 'from-blue-500 to-cyan-600'
        }
    ];

    const benefits = [
        'ANSI/OSHA Compliance Standards',
        'Quality-Assured Products',
        'Bulk Ordering Discounts',
        'Custom Branding Available',
        'Expert Safety Consulting',
        '24/7 Emergency Supply Support'
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-yellow-900 to-amber-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-yellow-400">PPE Solutions</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                Complete <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-400">PPE Solutions</span> for Data Centers
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Ensuring the safety of technicians, engineers, and facility teams with reliable Personal Protective Equipment solutions tailored for mission-critical data center environments.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-yellow-600 to-amber-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-yellow-500/30 transition-all duration-300">
                                    Request Quote
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-yellow-600/20 to-amber-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="/serviceImages/ppe/ppe-hero.jpg"
                                    alt="PPE Solutions"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-3xl p-12 border border-yellow-200">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why PPE Solutions Matter</h2>
                        <p className="text-lg text-gray-700 leading-relaxed mb-6">
                            In today's fast-paced digital world, data centers are the backbone of business operations. Ensuring the safety of technicians, engineers, and facility teams is critical to maintaining uninterrupted performance. At Nextgen Systems, we provide reliable Personal Protective Equipment (PPE) solutions tailored for data center environments.
                        </p>
                        <p className="text-lg text-gray-700 leading-relaxed">
                            We understand the importance of safety, compliance, and operational readiness in mission-critical facilities. Our PPE supply solutions support technicians, contractors, and facility teams working in active data center environments by ensuring access to high-quality protective gear that meets industry standards.
                        </p>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold mb-4">Our Products</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Comprehensive PPE Product Range</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Complete selection of safety equipment meeting ANSI/OSHA standards and industry best practices
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {products.map((product, index) => (
                            <div
                                key={index}
                                className="group relative bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-2xl hover:shadow-yellow-500/10 transition-all duration-500 hover:-translate-y-2"
                            >
                                <div className="p-8">
                                    {/* Icon */}
                                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${product.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                        {product.icon}
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-yellow-600 transition-colors">
                                        {product.title}
                                    </h3>

                                    {/* Description */}
                                    <p className="text-gray-600 leading-relaxed mb-6">
                                        {product.description}
                                    </p>

                                    {/* Link */}
                                    <Link
                                        to="/contact"
                                        className="inline-flex items-center gap-2 text-yellow-600 font-semibold group-hover:text-amber-600 transition-colors"
                                    >
                                        Inquire Now
                                        <FaArrowRight className="group-hover:translate-x-2 transition-transform" size={12} />
                                    </Link>
                                </div>

                                {/* Bottom gradient line */}
                                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${product.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <img
                                src="/serviceImages/ppe/ppe-safety.jpg"
                                alt="Safety Compliance"
                                className="w-full h-auto rounded-2xl shadow-2xl"
                            />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold mb-4">Benefits</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Why Choose Our PPE Solutions</h2>

                            <div className="space-y-6">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-start gap-4">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-600 to-amber-600 flex items-center justify-center text-white flex-shrink-0 mt-1">
                                            <FaCheckCircle size={16} />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">{benefit}</h3>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-gradient-to-r from-yellow-600 to-amber-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-yellow-500/30 transition-all duration-300"
                            >
                                Get a Free Assessment
                                <FaArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative bg-gradient-to-r from-yellow-600 to-amber-600 py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">Ready to Protect Your Team?</h2>
                    <p className="text-xl text-white/90 mb-12">
                        Let us help you find the right PPE solutions for your data center operations. Contact us today for a free consultation.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-yellow-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
                        >
                            Contact Us Today
                            <FaArrowRight size={14} />
                        </Link>
                        <a
                            href="tel:+12014099845"
                            className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300"
                        >
                            <FaPhone size={14} />
                            Call Us Now
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default PPESolutionsPage;
